// Масштабирование макета на краях диапазона ширин (RBXGD-1102).
//
// Перенос с Дерева: src/shared/utils/viewportScaler.ts. Там это TS-класс без
// привязки к фреймворку, здесь он же на обычном JS — composition API у нас нет,
// но классу он и не нужен.
//
// Две независимые задачи в одном месте, потому что обе решаются пересчётом при
// одном и том же событии — изменении ширины экрана.
//
// 1. Узкие экраны. Ниже 360px верстаем страницу как 360 и отдаём браузеру
//    масштаб под фактическую ширину. Отдельный CSS под 320 становится не нужен,
//    а раскладка перестаёт зависеть от того, насколько экран уже расчётного.
//
// 2. Широкие экраны. Выше 2560px увеличиваем макет целиком. Колонка контента у
//    нас 1440px, и на 3440 она занимает 42% экрана при базовом кегле 10px —
//    именно это в тикете названо «висит в пустоте по центру». Задача не
//    растянуть вёрстку на всю ширину (поля по бокам остаются), а вернуть
//    читаемый размер на плотном мониторе.

/** Ниже этой ширины фиксируем макет */
const BREAKPOINT = 360
/** Ширина, по которой верстается страница в фиксированном режиме */
const FIXED_WIDTH = 360

/** С этой ширины начинаем увеличивать макет (2K) */
const WIDE_BREAKPOINT = 2560
/**
 * Точка отсчёта увеличения. Совпадает с брейкпоинтом: на самом 2560 макет
 * остаётся как есть и растёт только дальше. От ширины вёрстки (1440) считать
 * нельзя — на 2560 это сразу дало бы 1.78 и выглядело гигантски.
 */
const WIDE_BASE_WIDTH = 2560
/** Потолок увеличения: вдвое к 5K, дальше не растём */
const MAX_WIDE_ZOOM = 2

/** Обычный адаптивный режим */
const VIEWPORT_RESPONSIVE = 'width=device-width, initial-scale=1.0, maximum-scale=5.0, minimum-scale=1.0'

/** Дебаунс пересчёта при resize/orientationchange */
const RESIZE_DEBOUNCE = 150

export class ViewportScaler {
  constructor(config) {
    const options = config || {}

    this.breakpoint = options.breakpoint || BREAKPOINT
    this.fixedWidth = options.fixedWidth || FIXED_WIDTH
    this.enableScaling = options.enableScaling !== false
    this.wideBreakpoint = options.wideBreakpoint || WIDE_BREAKPOINT
    this.wideBaseWidth = options.wideBaseWidth || WIDE_BASE_WIDTH
    this.maxWideZoom = options.maxWideZoom || MAX_WIDE_ZOOM
    this.enableWideScaling = options.enableWideScaling !== false

    this.viewportMeta = this.getOrCreateViewportMeta()
    this.isMobile = false
    this.debounceId = 0

    this.onViewportChange = () => {
      if (this.debounceId) window.clearTimeout(this.debounceId)

      this.debounceId = window.setTimeout(() => this.updateViewport(), RESIZE_DEBOUNCE)
    }

    window.addEventListener('resize', this.onViewportChange)
    window.addEventListener('orientationchange', this.onViewportChange)
    // pageshow, а не load: покрывает и обычную загрузку, и возврат из bfcache,
    // после которого ширина может быть другой.
    window.addEventListener('pageshow', this.onViewportChange)

    this.updateViewport()
  }

  getOrCreateViewportMeta() {
    let meta = document.querySelector('meta[name="viewport"]')

    if (!meta) {
      meta = document.createElement('meta')
      meta.name = 'viewport'
      document.head.appendChild(meta)
    }

    return meta
  }

  /**
   * Ширина устройства в CSS-пикселях, независимо от уже применённой меты:
   * visualViewport.width * scale инвариантен к width и initial-scale.
   *
   * Замер обязан обходиться без правки меты. Иначе каждая правка рождает resize,
   * resize запускает пересчёт, пересчёт снова правит мету — и это не сходится.
   */
  measureDeviceWidth() {
    const vv = window.visualViewport

    if (vv && vv.width && vv.scale) return Math.round(vv.width * vv.scale)

    // Фолбэк: в фиксированном режиме clientWidth равен fixedWidth и для замера
    // фактической ширины экрана не годится.
    return this.isMobile ? window.screen.width : document.documentElement.clientWidth || window.innerWidth
  }

  /**
   * Во сколько раз увеличивать макет на широком экране.
   *
   * Именно zoom, а не transform: scale — zoom масштабирует раскладку целиком,
   * не ломая position: fixed и не размывая текст. Мета viewport тут ни при чём,
   * десктопные браузеры её игнорируют.
   */
  wideZoom(deviceWidth) {
    if (!this.enableWideScaling || deviceWidth < this.wideBreakpoint) return 1

    return Math.min(Math.max(deviceWidth / this.wideBaseWidth, 1), this.maxWideZoom)
  }

  /** Пересчитывает режим. Мету пишет только при реальном изменении. */
  updateViewport() {
    const deviceWidth = this.measureDeviceWidth()

    // Нулевой замер — вкладка ещё не разложена. Трогать состояние нельзя: иначе
    // страница уедет в фиксированный режим с масштабом 0 и останется в нём.
    if (deviceWidth <= 0) return

    this.isMobile = deviceWidth < this.breakpoint

    const fixed = this.isMobile && this.enableScaling

    let target = VIEWPORT_RESPONSIVE

    if (fixed) {
      const scale = (deviceWidth / this.fixedWidth).toFixed(3)

      target = `width=${this.fixedWidth}, initial-scale=${scale}, minimum-scale=${scale}, maximum-scale=${scale}, user-scalable=no`
    }

    if (this.viewportMeta.content !== target) this.viewportMeta.content = target

    const zoom = fixed ? 1 : this.wideZoom(deviceWidth)
    const root = document.documentElement

    root.style.setProperty('--app-zoom', zoom.toFixed(3))
    root.classList.toggle('viewport-fixed', fixed)
    root.classList.toggle('viewport-responsive', !fixed)
    root.classList.toggle('viewport-wide', zoom > 1)
  }

  destroy() {
    window.removeEventListener('resize', this.onViewportChange)
    window.removeEventListener('orientationchange', this.onViewportChange)
    window.removeEventListener('pageshow', this.onViewportChange)

    if (this.debounceId) window.clearTimeout(this.debounceId)

    document.documentElement.classList.remove('viewport-fixed', 'viewport-responsive', 'viewport-wide')
    document.documentElement.style.removeProperty('--app-zoom')
  }
}

/**
 * Создаёт и запускает скейлер. Возвращает экземпляр — он нужен, чтобы вызвать
 * destroy(), и чтобы создание не выглядело как new ради побочного эффекта.
 */
export function setupViewportScaler(config) {
  return new ViewportScaler(config)
}

export default setupViewportScaler
